import { Injectable } from "@angular/core";
// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// order -entry
import { OENOperationMode } from "../data/enums/oe-operation-mode.enum";
import { OENActionCommand } from "../data/enums/oe-action-command.enum";
import { OELoadingResponseModel } from "../data/models/oe-loading-response.model";
import { OENLoadingOutcome } from "../data/enums/oe-loading-outcome.enum";
import { OEModelGetterService } from "./model/oe-model-getter.service";
import { OEModelSetterService } from "./model/oe-model-setter.service";
import { OEModelOperationsService } from "./model/oe-model-operations.service";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { OEUtilityService } from "./basic/oe-utility.service";
import { OETimeMachineService } from "./basic/oe-time-machine.service";
import { OECoordinatorService } from "./oe-coordinator.service";
import { OELoaderService } from "./oe-loader.service";
import { OEWorkerActionsService } from "./worker/oe-worker-actions.service";
import { OEWorkerListenerService } from "./worker/oe-worker-listener.service";
import { OESyncService } from "./oe-sync.service";
import { OEValidatorService } from "./oe-validator.service";
import { OEPermissionsService } from "./basic/oe-permissions.service";
import { OESettingsService } from "./basic/oe-settings.service";
import { OEInputModel } from "../data/models/oe-input.model";
import { OENDisplayState } from "../data/enums/oe-display-state.enum";
import { OENCommand } from "../data/enums/oe-command.enum";
import {
  OELockingProfileModel,
  OEPrivatizationLockingProfileModel,
} from "../data/models/oe-locking-profile.model";
import { OEDataModel } from "../data/models/oe-data.model";
import { OELookupService } from "./basic/oe-lookup.service";
import { OELoggerService } from "./basic/oe-logger.service";
import { OEModelSyncService } from "./model/oe-model-sync.service";
import { OECloserService } from "./oe-closer.service";
import { OEActionCommandModel } from "../data/models/oe-action-command.model";
import { OEDLoadingDataModel } from "../data/models/oe-loading-data.model";
import { OENEntryType } from "../data/enums/oe-entry-type.enum";
import { BenchmarkDataPrimaryLookupService } from "shared/cubiq-services";
import { ScratchPadDataActionPrimaryService } from "../../../shared/cubiq-services/action/impl/ScratchPadDataActionPrimaryService";
import { OEApiService } from "./oe-api.service";
import { OEScreenRegistryService } from "./basic/oe-screen-registry.service";
import { OEScreenRegistryData } from "../data/models/oe-screen-registry.model";
import { OEModelLockingService } from "./model/oe-model-locking.service";

export const SYNTH_KILL_DISALLOWED_STATES = [OENDisplayState.SHOW_PROCESSING];

@Injectable()
export class OEActionsService {
  private _cmdTimeoutId: null | NodeJS.Timeout = null;

  private get cmdTimeoutId() {
    return this._cmdTimeoutId;
  }

  private set cmdTimeoutId(id: null | NodeJS.Timeout) {
    if (this._cmdTimeoutId) clearTimeout(this._cmdTimeoutId);

    this._cmdTimeoutId = id;
  }

  private control: {
    synthInterval: any;
    synthIntervalLoopCounter: number;
    synthIntervalCheckScreenRegistry: number;
    synthIntervalLoopLimit: number;
  } = {
    synthInterval: null,
    synthIntervalLoopCounter: 0,
    synthIntervalCheckScreenRegistry: 25, // 2,5 sec // keep it below limit
    synthIntervalLoopLimit: 50, // 5 sec.
  };

  /**********************************************************************/
  /* CONSTRUCTOR  */
  constructor(
    private getter: OEModelGetterService,
    private setter: OEModelSetterService,
    private operations: OEModelOperationsService,
    private utility: OEUtilityService,
    private timeMachine: OETimeMachineService,
    private coordinator: OECoordinatorService,
    private loader: OELoaderService,
    private workerActions: OEWorkerActionsService,
    private workerListener: OEWorkerListenerService,
    private sync: OESyncService,
    private modelSync: OEModelSyncService,
    private validator: OEValidatorService,
    private permissions: OEPermissionsService,
    private settings: OESettingsService,
    private lookup: OELookupService,
    private closer: OECloserService,
    private oeLogger: OELoggerService,

    private scratchpadService: ScratchPadDataActionPrimaryService,
    private _bmarkDataLookup: BenchmarkDataPrimaryLookupService,
    private screenRegistry: OEScreenRegistryService,
    private locker: OEModelLockingService,
  ) {
    this.oeLogger = oeLogger.for(__dirname);
    this.subscribeToCloserService();
  }

  private subscribeToCloserService(): void {
    this.closer.actionCtrl$.subscribe(($event: OEActionCommandModel) => {
      if ($event != null) {
        switch ($event.command) {
          case OENActionCommand.ABORT: // $event.value should contain refIds
            this.abort($event.value);
            break;
          case OENActionCommand.DESTROY_INSTANCES:
            this.destroyInstances($event.value);
            break;
          case OENActionCommand.UNLOCK_MODIFY: // $event.value should contain orderIds
            this.unlockModify($event.value, $event.isBmark);
            break;
          case OENActionCommand.UNLOCK_AMEND_MARKUP: // $event.value should contain orderIds
            this.unlockAmendMarkup($event.value);
            break;
          default:
            logger.out.error(
              "ERROR: oeActions.subscribeToCloser(): Command not implemented! command=%",
              OENActionCommand[$event.command],
            );
            break;
        }
      }
    });
  }

  /**********************************************************************/
  /* NEW  */
  public newInstance(refId: string, entryType: OENEntryType): void {
    try {
      let newRefId =
        typeof entryType === "number"
          ? this.operations.addEmptyInstanceType(entryType)
          : this.operations.addEmptyInstance();
      this.locker.createLockingProfileForNewEntry(refId);
      this.coordinator.cmdNewModel(refId, newRefId);

      if (this.getter.hasStrategy(refId)) {
        this.destroyInstances(refId);
      } else if (this.getter.has(refId)) {
        this.destroyInstance(refId);
      }

      // update the strategy panel => should be removed
      setTimeout(() => {
        this.coordinator.cmdDisplayStrategyLayoutChange(newRefId, OENCommand.PANEL_HIDE_STRATEGY);
      }, 100);
      // update the interface controls to initial state
      setTimeout(() => {
        this.coordinator.cmdChangeDisplayState(newRefId, OENCommand.STATE_ORDER_ENTRY);
      }, 150);
      setTimeout(() => {
        this.coordinator.cmdResetAllInterfaceControls(newRefId);
      }, 200);
      // load order entry defaults
      setTimeout(() => {
        this.loader.loadUserDefaults(newRefId);
      }, 300);

      //logger.out.info("DEV-TEST: actionNewOrder()-> END: ModelMap = ", this.getter.getAll());
    } catch (ex) {
      logger.out.error("ERROR: _oeActions.actionNewOrder(): Exception = ", ex);
    }
  }

  /**********************************************************************/
  /* LOAD  */

  /**
   * Public entry point for MODIFY / DUPLICATE / MODIFY INFO
   * -------------------------------------------------------------------
   * method chain across _oe services
   * 1. [+] _oeActions.loadOrderEntryModelForOrderId() -> _oeActions.invokeSynthesizeOrder();
   * 2. [+ ->] workerActions.synthesizeOrderEntryModelForOrderId();
   * 3. [+ -> ->] getter.addOrderEntryModel();
   * 4. [+ -> -> ->] utility.adaptOrderDataToOrderEntryModel(rootOrderData, mode);
   *
   */

  // CHANGE:
  public load(orderId: string, mode: OENOperationMode): Promise<OELoadingResponseModel> {
    return new Promise((res, rej) => {
      if (mode === OENOperationMode.NEW) {
        res(
          new OELoadingResponseModel(
            OENLoadingOutcome.ERROR,
            "Load order operation not supported for NEW order entry mode!",
          ),
        );
      } else if (mode === OENOperationMode.AMEND_MARKUP) {
        let interventionEventId = orderId;
        this.workerActions.getInterventionPreInitializationData(interventionEventId).then(
          (data: any) => {
            if (
              data != null &&
              data.orderData != null &&
              data.orderData instanceof CubiqTS.OrderDataImpl &&
              data.orderData.clOrdID != null
            ) {
              let resRefId = data.orderData.clOrdID;
              orderId = data.orderData.orderId.toString();
              logger.out.info(
                `DEV-TEST: InterventionEvent resolved eventId '${interventionEventId}' => orderId '${orderId}'`,
              );
              logger.out.info(`DEV-TEST: Order resolved orderId '${orderId}' => refId ${resRefId}`);

              if (this.getter.has(resRefId) && this.getter.isOperationModeAmendMarkup(resRefId)) {
                res(
                  new OELoadingResponseModel(
                    OENLoadingOutcome.WARNING_BREAK,
                    `Amend Markup for ${resRefId} already open!`,
                  ),
                );
              } else {
                this.invokeSynthesizeOrder(orderId, mode, resRefId, data).then(
                  (result: any) => {
                    this.coordinator.cmdDisplayStrategyLayoutChange(
                      resRefId,
                      OENCommand.PANEL_SHOW_STRATEGY,
                    );
                    res(result);
                  },
                  (reject) => {
                    rej(reject);
                  },
                );
              }
            } else {
              res(
                new OELoadingResponseModel(
                  OENLoadingOutcome.ERROR,
                  "Could not resolve orderData for intervention event!",
                ),
              );
            }
          },
          (rejectResolveRefId) => {
            rej(rejectResolveRefId);
          },
        );
      } else {
        // MODIFY, MODIFY INFO, DUPLICATE, MONITOR_EXECUTION
        //this.workerActions.resolveRefIdForOrderId(orderId).then((resRefId: string)=>{
        this.workerActions.getOrderPreInitializationData(orderId, mode).then(
          (data: any) => {
            if (data != null && this.getter.validateRefFormat(data.ref)) {
              let resRefId = data.ref;
              const rootRefId = this.getter.has(resRefId)
                ? this.getter.getRootRefIdForStrategyRefId(resRefId)
                : null;

              let displayedId: string;
              if (mode === OENOperationMode.MONITOR_EXECUTION) {
                // If we re reverting back to display mode then keep current order leg active
                displayedId = rootRefId
                  ? this.getter.getActivelyDisplayedRefId(rootRefId)
                  : resRefId;
              } else {
                // If we are in any other mode then activate the specified leg
                displayedId = resRefId;
              }

              logger.out.info(
                `DEV-TEST: Order resolved orderId '${orderId}' => refId '${resRefId}'`,
              );

              // Order is active and supports execution monitoring
              const monitoringExisting =
                this.getter.has(resRefId) && this.getter.supportsMonitoringExecution(resRefId);

              // close order entry NEW  if modify is invoked on the open order entry new  for rerRefId, then init
              //New fix for OMS-25348
              const existingModify: boolean =
                mode === OENOperationMode.MODIFY &&
                this.getter.has(resRefId) &&
                (this.getter.isOperationModeModify(resRefId) ||
                  (!this.getter.isOperationModeModify(resRefId) &&
                    this.getter.isOrderDisplayStateTerminal(resRefId)));

              const modifyKill: boolean = existingModify && !monitoringExisting;

              // kil modify window if its terminal when modify info is issued
              let modifyKill4ModifyInfo: boolean =
                mode === OENOperationMode.MODIFY_INFO &&
                this.getter.has(resRefId) &&
                this.getter.isOperationModeModify(resRefId) &&
                this.getter.isOrderDisplayStateTerminal(resRefId);
              // kill duplicate/new window if its terminal and and modify info is issued
              let duplicateKill4ModifyInfo: boolean =
                mode === OENOperationMode.MODIFY_INFO &&
                this.getter.has(resRefId) &&
                this.getter.isOrderDisplayStateTerminal(resRefId) &&
                (this.getter.isOperationModeDuplicate(resRefId) ||
                  this.getter.isOperationModeNew(resRefId));
              // kill modify info if open and in terminal state
              let modifyInfoKill: boolean =
                mode === OENOperationMode.MODIFY_INFO &&
                this.getter.has(resRefId) &&
                this.getter.isOperationModeModifyInfo(resRefId) &&
                this.getter.isOrderDisplayStateTerminal(resRefId);
              // skip initialization  for modify info if open and active
              let modifyInfoSkip: boolean =
                mode === OENOperationMode.MODIFY_INFO &&
                this.getter.has(resRefId) &&
                this.getter.isOperationModeModifyInfo(resRefId) &&
                !this.getter.isOrderDisplayStateTerminal(resRefId);
              // skip initialization  for modify info if open and active
              let modifySkipScratchpadOrder: boolean =
                (mode === OENOperationMode.MODIFY_SCRATCHPAD || mode === OENOperationMode.EDIT) &&
                this.getter.has(resRefId) &&
                this.getter.isOperationModeModify(resRefId) &&
                !this.getter.isOrderDisplayStateTerminal(resRefId);
              // as of: https://www.bcdfx.com/ijira/browse/OMS-31286 - Order Entry should close and reopen when in terminal state
              let modifyKillScratchpadOrder: boolean =
                (mode === OENOperationMode.MODIFY_SCRATCHPAD || mode === OENOperationMode.EDIT) &&
                this.getter.has(resRefId) &&
                (!this.getter.isOperationModeModify(resRefId) ||
                  (this.getter.isOperationModeModify(resRefId) &&
                    this.getter.isOrderDisplayStateTerminal(resRefId)));

              if (modifyInfoSkip) {
                this.coordinator.cmdActivateStrategyLegRefListener(resRefId);
                res(
                  new OELoadingResponseModel(
                    OENLoadingOutcome.WARNING_BREAK,
                    `Modify-Info Window already active for ${resRefId}!`,
                  ),
                );
                return;
              }

              // We need to unlock any locked order before attempting the edit - Promise is empty unless order
              // needs to be unlocked
              let unlockingExistingOrder: Promise<any>;
              if (monitoringExisting) {
                // We are aleady monitoring the order in a terminal state window
                // Need to tidy up model references immedaitely otherwise we'll get a clash
                if (
                  this.getter.isLockedForModification(resRefId) &&
                  this.getter.isOperationModeModify(resRefId)
                ) {
                  unlockingExistingOrder = this.workerActions.submitUnlockOrdersOnModify(
                    [orderId],
                    false,
                  );
                } else {
                  unlockingExistingOrder = Promise.resolve(true);
                }
                this.destroyInstances(resRefId, false);
              } else {
                unlockingExistingOrder = Promise.resolve(true);
              }
              if (modifySkipScratchpadOrder) {
                res(
                  new OELoadingResponseModel(
                    OENLoadingOutcome.WARNING_BREAK,
                    `Scratchpad Edit Window already active for ${resRefId}!`,
                  ),
                );
                this.coordinator.cmdFocusOrderEntryScreen(resRefId);
              } else {
                if (
                  !monitoringExisting &&
                  (modifyKill ||
                    modifyInfoKill ||
                    modifyKill4ModifyInfo ||
                    duplicateKill4ModifyInfo ||
                    modifyKillScratchpadOrder)
                ) {
                  this.coordinator.cmdCloseOrderEntryScreen(resRefId);
                  this.control.synthInterval = setInterval(() => {
                    if (!this.getter.has(resRefId)) {
                      this.control.synthIntervalLoopCounter = 0; // reset due to persistance, note: parallel looping for 2+ load actions not properly handled
                      if (this.control.synthInterval != null) {
                        clearInterval(this.control.synthInterval);
                      }

                      this.invokeSynthesizeOrder(orderId, mode, resRefId, data).then(
                        (result: any) => {
                          res(result);
                        },
                        (reject) => {
                          rej(reject);
                        },
                      );
                    } else {
                      // error handling to avoid endless loop, if open model cannot be destroyed for some unknown reason
                      this.control.synthIntervalLoopCounter++;
                      if (
                        this.control.synthIntervalLoopCounter > this.control.synthIntervalLoopLimit
                      ) {
                        this.control.synthIntervalLoopCounter = 0; // reset due to persistance, note: parallel looping for 2+ load actions not properly handled
                        if (this.control.synthInterval != null) {
                          clearInterval(this.control.synthInterval);
                        }
                        res(
                          new OELoadingResponseModel(
                            OENLoadingOutcome.ERROR,
                            "Existing model failed to be destroyed, breaking endless loop procedure in effect!",
                          ),
                        );
                      } else if (
                        this.control.synthIntervalLoopCounter ===
                        this.control.synthIntervalCheckScreenRegistry
                      ) {
                        if (!this.screenRegistry.has(resRefId)) {
                          // if no screen delete
                          this.destroyInstances(resRefId);
                        } else {
                          this.screenRegistry.updateFull(resRefId);
                          let screenData = this.screenRegistry.get(resRefId);
                          if (
                            screenData instanceof OEScreenRegistryData &&
                            !SYNTH_KILL_DISALLOWED_STATES.includes(screenData.displayState)
                          ) {
                            this.destroyInstances(resRefId);
                          }
                        }
                      }
                    }
                  }, 100);
                } else {
                  // Only want to load in the order when we have a return from the unlock
                  unlockingExistingOrder.then(() => {
                    // order entry New is not displayed for resRefId, so just continue with init
                    this.invokeSynthesizeOrder(orderId, mode, rootRefId ?? resRefId, data).then(
                      (result: any) => {
                        if (displayedId) {
                          this.coordinator.cmdActivateStrategyLegRefListener(displayedId);
                        }
                        res(result);
                      },
                      (reject) => {
                        rej(reject);
                      },
                    );
                  });
                }
              }
            } else {
              res(
                new OELoadingResponseModel(
                  OENLoadingOutcome.ERROR,
                  `Invalid resolution of orderId: '${orderId}' for refId!`,
                ),
              );
            }
          },
          (rejectResolveRefId) => {
            rej(rejectResolveRefId);
          },
        );
      }
    });
  }

  private invokeSynthesizeOrder(
    orderId: string,
    mode: OENOperationMode,
    resRefId: string,
    data: any = null,
  ): Promise<OELoadingResponseModel> {
    return new Promise((res, rej) => {
      // pre-initialization procedure for MODIFY and MODIFY_SCRATCHPAD
      if (mode === OENOperationMode.MODIFY || mode === OENOperationMode.MODIFY_SCRATCHPAD) {
        // Create new blank model
        this.operations.addEmptyInstance(resRefId);
        // set privatized
        if (
          data != null &&
          data.privatized != null &&
          typeof data.privatized === "boolean" &&
          data.privatized
        )
          this.setter.setModificationPrivatized(resRefId);
        // set display mode to modify
        this.setter.setOperationModeToModify(resRefId);
        // mark in initialization
        this.setter.startInInitialization(resRefId);
        //  initialize subscription
        this.workerListener.initializeWorkerLifeCycleSubscriptionsForStrategy(resRefId);
      } else if (mode === OENOperationMode.AMEND_MARKUP) {
        // Create new blank model
        //this.getter.registerNewOrderEntryInstance(resRefId);
        // set display mode to modify
        //this.setter.setOperationModeToAmendMarkup(resRefId);
        //  initialize subscription
        this.workerListener.initializeWorkerInterventionSubscriptionsForStrategy(resRefId);
      }

      /////////////////////////////////////////////////////
      // load the order data
      this.workerActions.synthesizeOrderEntryModelForOrderId(orderId, mode, resRefId, data).then(
        (synthData: any) => {
          let rootRefId =
            typeof synthData === "string"
              ? synthData
              : typeof synthData === "object" && synthData.hasOwnProperty("id")
              ? synthData.id
              : null;

          this.setter.stopInInitialization(resRefId);
          if (this.getter.validateRefFormat(rootRefId)) {
            let entryType = this.getter.getEntryType(rootRefId);
            // re-initialize subscription if not killed, needed for strategy nodes
            let subscriptionActive: boolean =
              this.sync.latestEventUpdate_getSubscriptionLifeCycleActive(resRefId);
            if (subscriptionActive == null || subscriptionActive) {
              this.workerListener.initializeWorkerLifeCycleSubscriptionsForStrategy(resRefId);
            }
            //  re-initialize intervention subscription for amend markup, needed for nodes
            if (mode === OENOperationMode.AMEND_MARKUP) {
              this.workerListener.initializeWorkerInterventionSubscriptionsForStrategy(resRefId);
            }

            // see blocking mechanism explanation below
            this.modelSync.addToBlockUpdateList(rootRefId);
            // setting meta-data about the orderId upon modify or modify info was issued
            if (mode !== OENOperationMode.DUPLICATE) {
              // MODIFY, MODIFY INFO, AMEND_MARKUP
              this.setter.setModifyIssuerOrderId(rootRefId, orderId); // issuer orderId set all strategy legs
            }

            if (mode === OENOperationMode.MODIFY || mode === OENOperationMode.MODIFY_SCRATCHPAD) {
              // MODIFY
              let refId: string = this.getter.findRefIdForOrderId(orderId, rootRefId);

              if (refId == null || typeof refId !== "string") {
                refId = rootRefId;
                logger.out.error(
                  `ERROR: _oeActions: Could not find refId for orderId: '${orderId}', loading continued with rootRefId: '${rootRefId}'`,
                );
              }
              let privatized = data?.privatized == true;
              let errors = new Array<OEInputModel>();
              let dataValid = this.validator.validateOrderData(refId, errors, true, privatized);
              let displayState: any = this.getter.getDisplayState(refId);
              // leave this log
              logger.out.info(
                `MODIFY - LOAD: _oeActions: OrderId: '${orderId}' | EntryType: '${
                  OENEntryType[entryType]
                }' | RefId: '${refId}' | RootRefId:  '${rootRefId}' | State: '${
                  OENDisplayState[displayState]
                }' | valid ${dataValid ? "YES" : "NO"} | errors:${
                  errors != null && errors.length > 0 ? " %O" : " NO"
                }`,
                errors,
              );

              // handle errors
              if (!dataValid && errors != null && errors.length > 0) {
                setTimeout(() => {
                  this.coordinator.cmdInvalidInput(refId, errors);
                }, 100);
                res(
                  new OELoadingResponseModel(
                    OENLoadingOutcome.SUCCESS,
                    null,
                    rootRefId,
                    refId,
                    entryType,
                  ),
                );
              } else if (!dataValid || !this.getter.has(refId)) {
                /// INITIALIZATION ERROR (invalid but no errors)
                if (this.getter.has(refId)) {
                  this.destroyInstances(refId);
                }
                res(new OELoadingResponseModel(OENLoadingOutcome.ERROR, "Invalid model"));
              }

              if (this.getter.has(refId)) {
                // apply display state based on the server order state
                if (
                  displayState === OENDisplayState.SHOW_MODIFY_OUT_ACCEPTANCE ||
                  displayState === OENDisplayState.SHOW_APPROVAL_EXTERNAL_MODIFY
                ) {
                  let traderGroupCodeId: string = this.getter.instance(refId).traderGroupCodeId;
                  let srvOrderState: CubiqTS.OrderState = this.getter.instance(refId).srvOrderState;

                  let promiseArray = [
                    this.permissions.determineAcceptanceNeededPermissions(
                      CubiqTS.AcceptanceAction.ACCEPT,
                      traderGroupCodeId,
                      srvOrderState,
                    ),
                    this.permissions.determineAcceptanceNeededPermissions(
                      CubiqTS.AcceptanceAction.REJECT,
                      traderGroupCodeId,
                      srvOrderState,
                    ),
                    this.permissions.determineAcceptanceNeededPermissions(
                      CubiqTS.AcceptanceAction.ABORT,
                      traderGroupCodeId,
                      srvOrderState,
                    ),
                  ];
                  Promise.all(promiseArray)
                    .then(
                      (values: Array<boolean>) => {
                        let acceptPermission: boolean = values[0];
                        let rejectPermission: boolean = values[1];
                        let abortPermission: boolean = values[2];
                        let externalGroupAndState: boolean =
                          this.lookup.determineExternalTraderGroupNow(traderGroupCodeId) &&
                          (srvOrderState === CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED ||
                            srvOrderState === CubiqTS.OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED ||
                            srvOrderState === CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED);

                        this.cmdTimeoutId = setTimeout(() => {
                          // if timeout is removed the display state is not applied (oe window not displayed yet)
                          let currentDisplayState = this.getter.getDisplayState(refId);
                          let latestEventState = this.sync.latestEventUpdate_getDisplayState(refId);
                          if (
                            (currentDisplayState === OENDisplayState.SHOW_MODIFY_OUT_ACCEPTANCE ||
                              currentDisplayState ===
                                OENDisplayState.SHOW_APPROVAL_EXTERNAL_MODIFY) &&
                            (latestEventState == null || currentDisplayState === latestEventState)
                          ) {
                            let payloadData =
                              typeof synthData === "object" &&
                              synthData.hasOwnProperty("tooltip") &&
                              typeof synthData.tooltip === "string" &&
                              synthData.tooltip.length > 1
                                ? {
                                    acceptance: acceptPermission,
                                    rejection: rejectPermission,
                                    abort: abortPermission,
                                    tooltip: synthData.tooltip,
                                  }
                                : {
                                    acceptance: acceptPermission,
                                    rejection: rejectPermission,
                                    abort: abortPermission,
                                  };
                            if (externalGroupAndState) {
                              this.coordinator.cmdChangeDisplayState(
                                refId,
                                OENCommand.STATE_ORDER_EXT_MODIFY_OUT_ACCEPTANCE,
                                payloadData,
                              );
                              this.coordinator.cmdLockInterfaceControls(refId, false);
                            } else {
                              this.coordinator.cmdChangeDisplayState(
                                refId,
                                OENCommand.STATE_ORDER_MODIFY_OUT_ACCEPTANCE,
                                payloadData,
                              );
                              this.coordinator.cmdLockInterfaceControls(refId, false);
                            }
                          } else if (
                            currentDisplayState === OENDisplayState.SHOW_APPROVAL_EXTERNAL_MODIFY &&
                            latestEventState === OENDisplayState.SHOW_ORDER_ENTRY
                          ) {
                            // auto-accepted by server while being synthesized
                            let event = this.sync.popLatestEventUpdateAndClearQueue(refId);
                            this.coordinator.cmdChangeDisplayState(refId, event.cmdStateChange);
                          } else {
                            this.oeLogger.outputDevLogToConsole(
                              `${this.constructor.name} unmatched sync state`,
                              true,
                            );
                          }
                        }, 300);
                        //this.getter.stopInInitialization(refId);
                        res(
                          new OELoadingResponseModel(
                            OENLoadingOutcome.SUCCESS,
                            null,
                            rootRefId,
                            refId,
                            entryType,
                          ),
                        );
                      },
                      (rejection) => {
                        this.coordinator.cmdChangeDisplayState(
                          refId,
                          OENCommand.STATE_ORDER_INT_ERROR,
                          "Internal error: Permission promise rejected!",
                        );
                      },
                    )
                    .catch((reason) => {
                      this.coordinator.cmdChangeDisplayState(
                        refId,
                        OENCommand.STATE_ORDER_INT_ERROR,
                        "Internal error: exception = " + reason,
                      );
                    });
                } else if (displayState === OENDisplayState.SHOW_ORDER_ENTRY) {
                  this.cmdTimeoutId = setTimeout(() => {
                    let newDisplayState = this.getter.getDisplayState(refId);
                    let latestEventState = this.sync.latestEventUpdate_getDisplayState(refId);
                    if (
                      newDisplayState === OENDisplayState.SHOW_ORDER_ENTRY &&
                      (latestEventState != null || newDisplayState === latestEventState)
                    ) {
                      this.coordinator.cmdChangeDisplayState(
                        refId,
                        OENCommand.STATE_ORDER_IN_MODIFICATION,
                      );
                    } else {
                      this.oeLogger.outputDevLogToConsole(
                        `${this.constructor.name} synthesizeOrderEntryModelForOrderId ignoring matching state `,
                        true,
                      );
                    }
                  }, 300);
                  res(
                    new OELoadingResponseModel(
                      OENLoadingOutcome.SUCCESS,
                      null,
                      rootRefId,
                      refId,
                      entryType,
                    ),
                  );
                } else if (displayState === OENDisplayState.SHOW_REJECTED) {
                  this.cmdTimeoutId = setTimeout(() => {
                    let newDisplayState = this.getter.getDisplayState(refId);
                    let latestEventState = this.sync.latestEventUpdate_getDisplayState(refId);
                    if (
                      newDisplayState === OENDisplayState.SHOW_ORDER_ENTRY &&
                      (latestEventState != null || newDisplayState === latestEventState)
                    ) {
                      this.coordinator.cmdChangeDisplayState(
                        refId,
                        OENCommand.STATE_ORDER_REJECTED,
                      );
                    }
                  }, 300);
                  res(
                    new OELoadingResponseModel(
                      OENLoadingOutcome.SUCCESS,
                      null,
                      rootRefId,
                      refId,
                      entryType,
                    ),
                  );
                } else {
                  res(
                    new OELoadingResponseModel(
                      OENLoadingOutcome.ERROR,
                      `Invalid state OENDisplayState.${OENDisplayState[displayState]}`,
                    ),
                  );
                }
              } else {
                // !this.getter.has(refId) => true (model with that refId does not exist)
                res(new OELoadingResponseModel(OENLoadingOutcome.ERROR, "Invalid model"));
              }
            } else if (mode === OENOperationMode.DUPLICATE) {
              // DUPLICATE
              let errors = new Array<OEInputModel>();
              let privatizedSource: boolean =
                data != null && typeof data.privatized === "boolean" ? data.privatized : false;
              let dataValid = this.validator.validateOrderData(
                rootRefId,
                errors,
                true,
                privatizedSource,
              ); //privatizedSource
              // leave this log
              logger.out.info(
                `DUPLICATE - LOAD: _oeActions: OrderId: '${orderId}' | EntryType: '${
                  OENEntryType[entryType]
                }' | RefId: '${rootRefId}' | RootRefId: '${rootRefId}' | valid: ${
                  dataValid ? "YES" : "NO"
                } | errors:${errors != null && errors.length > 0 ? " %O" : " NO"}`,
                errors,
              );
              if (dataValid) {
                res(
                  new OELoadingResponseModel(
                    OENLoadingOutcome.SUCCESS,
                    null,
                    rootRefId,
                    rootRefId,
                    entryType,
                  ),
                );
              } else if (errors != null && errors.length > 0) {
                setTimeout(() => {
                  this.coordinator.cmdInvalidInput(rootRefId, errors);
                }, 200);
                res(
                  new OELoadingResponseModel(
                    OENLoadingOutcome.WARNING_CONTINUE,
                    "Input errors detected",
                    rootRefId,
                    rootRefId,
                    entryType,
                  ),
                );
              } else {
                /// INITIALIZATION ERROR
                if (this.getter.has(rootRefId)) {
                  this.destroyInstances(rootRefId);
                }
                res(new OELoadingResponseModel(OENLoadingOutcome.ERROR, "Invalid model"));
              }
            } else if (mode === OENOperationMode.EDIT) {
              // EDIT
              let errors = new Array<OEInputModel>();
              let privatizedSource: boolean =
                data != null && typeof data.privatized === "boolean" ? data.privatized : false;
              let dataValid = this.validator.validateOrderData(
                rootRefId,
                errors,
                true,
                privatizedSource,
              ); //privatizedSource
              // leave this log
              logger.out.info(
                `EDIT - LOAD: _oeActions: OrderId: '${orderId}' | EntryType: '${
                  OENEntryType[entryType]
                }' | RefId: '${rootRefId}' | RootRefId: '${rootRefId}' | valid: ${
                  dataValid ? "YES" : "NO"
                } | errors:${errors != null && errors.length > 0 ? " %O" : " NO"}`,
                errors,
              );
              if (dataValid) {
                res(
                  new OELoadingResponseModel(
                    OENLoadingOutcome.SUCCESS,
                    null,
                    rootRefId,
                    rootRefId,
                    entryType,
                  ),
                );
              } else if (errors != null && errors.length > 0) {
                setTimeout(() => {
                  this.coordinator.cmdInvalidInput(rootRefId, errors);
                }, 200);
                res(
                  new OELoadingResponseModel(
                    OENLoadingOutcome.WARNING_CONTINUE,
                    "Input errors detected",
                    rootRefId,
                    rootRefId,
                    entryType,
                  ),
                );
              } else {
                /// INITIALIZATION ERROR
                if (this.getter.has(rootRefId)) {
                  this.destroyInstances(rootRefId);
                }
                res(new OELoadingResponseModel(OENLoadingOutcome.ERROR, "Invalid model"));
              }
            } else if (mode === OENOperationMode.MODIFY_INFO) {
              // MODIFY INFO
              let errors = new Array<OEInputModel>();
              let refId: string = this.getter.findRefIdForOrderId(orderId, rootRefId);
              let dataValid = this.validator.validateOrderData(rootRefId, errors, true);
              // leave this log
              logger.out.info(
                `MODIFY INFO - LOAD: _oeActions: OrderId: '${orderId}' | EntryType: '${
                  OENEntryType[entryType]
                }' | RefId: '${refId}' | RootRefId:  '${rootRefId}' | valid ${
                  dataValid ? "YES" : "NO"
                } | errors:${errors != null && errors.length > 0 ? " %O" : " NO"}`,
                errors,
              );
              if (dataValid) {
                res(
                  new OELoadingResponseModel(
                    OENLoadingOutcome.SUCCESS,
                    null,
                    rootRefId,
                    refId,
                    entryType,
                  ),
                );
              } else if (errors != null && errors.length > 0) {
                setTimeout(() => {
                  this.coordinator.cmdInvalidInput(refId, errors);
                }, 200);
                res(
                  new OELoadingResponseModel(
                    OENLoadingOutcome.WARNING_CONTINUE,
                    "Input errors detected",
                    rootRefId,
                    refId,
                    entryType,
                  ),
                );
              } else {
                /// INITIALIZATION ERROR
                if (this.getter.has(rootRefId)) {
                  this.destroyInstances(rootRefId);
                }
                res(new OELoadingResponseModel(OENLoadingOutcome.ERROR, "Invalid model"));
              }
            } else if (mode === OENOperationMode.MONITOR_EXECUTION) {
              // MONITOR EXECUTION
              let errors = new Array<OEInputModel>();
              let refId: string = this.getter.findRefIdForOrderId(orderId, rootRefId);
              let dataValid = this.validator.validateOrderData(rootRefId, errors, true);
              logger.out.info(
                ` MONITOR EXECUTION - LOAD: _oeActions: OrderId: '${orderId}' | EntryType: '${
                  OENEntryType[entryType]
                }' | RefId: '${refId}' | RootRefId:  '${rootRefId}' | valid ${
                  dataValid ? "YES" : "NO"
                } | errors:${errors != null && errors.length > 0 ? " %O" : " NO"}`,
                errors,
              );

              if (dataValid) {
                res(
                  new OELoadingResponseModel(
                    OENLoadingOutcome.SUCCESS,
                    null,
                    rootRefId,
                    refId,
                    entryType,
                  ),
                );
              } else if (errors != null && errors.length > 0) {
                res(
                  new OELoadingResponseModel(
                    OENLoadingOutcome.WARNING_CONTINUE,
                    "Input errors detected",
                    rootRefId,
                    refId,
                    entryType,
                  ),
                );
                setTimeout(() => {
                  this.coordinator.cmdInvalidInput(refId, errors);
                }, 200);
              } else {
                /// INITIALIZATION ERROR
                res(new OELoadingResponseModel(OENLoadingOutcome.ERROR, "Invalid model"));
                if (this.getter.has(rootRefId)) {
                  this.destroyInstances(rootRefId);
                }
              }
            } else if (mode === OENOperationMode.AMEND_MARKUP) {
              // AMEND_MARKUP
              let errors = new Array<OEInputModel>();
              let refId: string = this.getter.findRefIdForOrderId(orderId, rootRefId);
              let privatizedSource: boolean = this.getter.isModificationPrivatized(refId);
              let dataValid = this.validator.validateOrderData(
                rootRefId,
                errors,
                true,
                privatizedSource,
              );
              // leave this log
              logger.out.info(
                `AMEND MARKUP - LOAD: _oeActions: OrderId: '${orderId}' | EntryType: '${
                  OENEntryType[entryType]
                }' | RefId: '${refId}' | RootRefId:  '${rootRefId}' | valid ${
                  dataValid ? "YES" : "NO"
                } | errors:${errors != null && errors.length > 0 ? " %O" : " NO"}`,
                errors,
              );
              if (dataValid) {
                res(
                  new OELoadingResponseModel(
                    OENLoadingOutcome.SUCCESS,
                    null,
                    rootRefId,
                    refId,
                    entryType,
                  ),
                );
              } else if (errors != null && errors.length > 0) {
                setTimeout(() => {
                  this.coordinator.cmdInvalidInput(refId, errors);
                }, 200);
                res(
                  new OELoadingResponseModel(
                    OENLoadingOutcome.WARNING_CONTINUE,
                    "Input errors detected",
                    rootRefId,
                    refId,
                    entryType,
                  ),
                );
              } else {
                /// INITIALIZATION ERROR
                if (this.getter.has(rootRefId)) {
                  this.destroyInstances(rootRefId);
                }
                res(new OELoadingResponseModel(OENLoadingOutcome.ERROR, "Invalid model"));
              }
            } else {
              if (this.getter.has(resRefId)) {
                this.destroyInstances(resRefId);
              }
              res(new OELoadingResponseModel(OENLoadingOutcome.ERROR, "Invalid order entry mode!"));
            }
          } else {
            res(new OELoadingResponseModel(OENLoadingOutcome.ERROR, "Invalid model!"));
          }
        },
        (reject) => {
          if (this.getter.has(resRefId)) {
            this.destroyInstances(resRefId);
          }
          rej(reject);
        },
      );
    });
  }

  // used only by scratchpad - for now
  public updateModelReference(oldReference: string, newReference: string): void {
    let oldRootRefId: string = this.getter.has(oldReference)
      ? this.getter.getRootIdForNodeWithRefId(oldReference)
      : null;

    if (oldRootRefId != null) {
      let newRefId: string = this.operations.copyInstanceStrategies(oldRootRefId, newReference);
      let newRootRefId = this.getter.getRootIdForNodeWithRefId(newRefId);
      this.coordinator.cmdNewModel(oldRootRefId, newRefId);

      // register strategy listeners
      setTimeout(() => {
        let strategyLegs: Array<string> = this.getter.getStrategyNodesListRoot(newRefId);
        if (strategyLegs.length > 1) {
          strategyLegs.forEach((legRefId) => {
            this.coordinator.cmdAddStrategyLegRefListener(newRootRefId, legRefId);
          });
          //this.coordinator.cmdDisplayStrategyLayoutChange(newRefId, OENCommand.PANEL_SHOW_STRATEGY);
          //setTimeout(
          this.coordinator.cmdActivateStrategyLegRefListener(newRefId);
          //, 300);
          this.coordinator.cmdRefreshStrategyLayout(newRefId);
        } else {
          this.coordinator.cmdDisplayStrategyLayoutChange(newRefId, OENCommand.PANEL_HIDE_STRATEGY);
        }
      }, 150);

      // activate/load strategy - probably not needed
      /* let model = this.getter.get(newRefId);
			let data: OEDLoadingDataModel = this.utility.createInterfaceLoadDataFromModel(model);
			let lockingProfile: OELockingProfileModel = OEPrivatizationLockingProfileModel.getLockIntersection(model.strategy.lockProfile, model.privatizationLockProfile); */

      // activate screen listeners -
      /* 	setTimeout(()=>{
				// load data and locking profile
				this.coordinator.cmdLoadInterfaceControlData(newRefId, data);
				this.coordinator.cmdLoadInterfaceControlLockingProfile(newRefId, lockingProfile);
			}, 300); */

      setTimeout(() => {
        if (this.getter.hasStrategy(oldRootRefId)) {
          this.destroyInstances(oldRootRefId);
        } else if (this.getter.has(oldRootRefId)) {
          this.destroyInstance(oldRootRefId);
        }
      }, 500);
    }
  }

  /**********************************************************************/
  /* COPY  */
  public copy(refId: string, setModeToNew: boolean = true, keepOrder: boolean = false): void {
    if (this.getter.has(refId)) {
      try {
        this.oeLogger.outputDevLogToConsole(
          "actionCopyOrder()-> START: ModelMap = ",
          false,
          this.getter.getAllRootRefs(),
        );

        let newRefId: string = keepOrder ? refId : this.operations.copyInstanceStrategies(refId);
        let newRootRefId = this.getter.getRootIdForNodeWithRefId(newRefId);

        // reset mode to new
        if (setModeToNew) {
          this.setter.setOperationModeToNew(newRefId, true);
          this.setter.removeLockForModification(newRefId);
          this.setter.removeModificationPrivatized(newRefId);
          this.setter.resetFillState(newRefId);
          this.setter.resetModifyIssuerOrderId(newRefId);
        }

        // reset state
        this.setter.setOrderDisplayState(newRootRefId, OENDisplayState.SHOW_ORDER_ENTRY);

        this.coordinator.cmdNewModel(refId, newRootRefId);
        setTimeout(() => {
          if (!keepOrder) {
            this.destroyInstances(refId);
          }
          this.coordinator.cmdChangeDisplayState(newRootRefId, OENCommand.STATE_ORDER_ENTRY);
        }, 100);

        // register strategy listeners
        setTimeout(() => {
          let strategyLegs: Array<string> = this.getter.getStrategyNodesListRoot(newRefId);
          if (strategyLegs.length > 1) {
            strategyLegs.forEach((legRefId) => {
              this.coordinator.cmdAddStrategyLegRefListener(newRootRefId, legRefId);
            });
            //this.coordinator.cmdDisplayStrategyLayoutChange(newRefId, OENCommand.PANEL_SHOW_STRATEGY);
            //setTimeout(
            this.coordinator.cmdActivateStrategyLegRefListener(newRefId);
            //, 300);
            this.coordinator.cmdRefreshStrategyLayout(newRefId);
          } else {
            this.coordinator.cmdDisplayStrategyLayoutChange(
              newRefId,
              OENCommand.PANEL_HIDE_STRATEGY,
            );
          }
        }, 150);
        // reload strategy panel
        //setTimeout(()=> { this.coordinator.cmdRefreshStrategyLayout(newRefId);}, 200);
        // activate/load strategy
        let model = this.getter.get(newRefId);
        let data: OEDLoadingDataModel = this.utility.createInterfaceLoadDataFromModel(model);
        let lockingProfile: OELockingProfileModel =
          OEPrivatizationLockingProfileModel.getLockIntersection(
            model.strategy.lockProfile,
            model.privatizationLockProfile,
          );
        // activate screen listeners
        //setTimeout(this.coordinator.cmdActivateStrategyLegRefListener(newRefId), 300);
        setTimeout(() => {
          // load data and locking profile
          this.coordinator.cmdLoadInterfaceControlData(newRefId, data);
          this.coordinator.cmdLoadInterfaceControlLockingProfile(newRefId, lockingProfile);
          this.coordinator.cmdUnbulk(newRefId, null);
        }, 300);

        setTimeout(() => {
          // load data and locking profile
          this.coordinator.cmdUpdateStrategyButtonsLockingWithMKTCheck(newRefId, true);
        }, 400);
      } catch (ex) {
        logger.out.error("ERROR: _oeActions.actionCopyOrder(): Exception = ", ex);
      }
    } else {
      logger.out.error(
        'ERROR: _oeActions.actionCopyOrder(): RefId "' + refId + '" invalid or not found!',
      );
    }
  }

  /**********************************************************************/
  /* DUPLICATE  */

  public duplicate(oeApiService: OEApiService, refId: string, setModeToNew: boolean = true): void {
    if (this.getter.has(refId)) {
      try {
        let newRefId: string = this.operations.copyInstanceStrategies(refId, null, true);
        let sourceOperation: OENOperationMode = this.getter.getOperationMode(refId);
        // reset mode to new
        if (setModeToNew) {
          this.setter.setOperationModeToNew(newRefId, true);
        } else {
          this.setter.setOperationModeToDuplicate(newRefId, true);
          // block updates on new root
          this.modelSync.addToBlockUpdateList(newRefId);
        }

        let oeDataModel = this.getter.get(newRefId);
        let isAlgo: boolean = false;
        let isLimit: boolean = false;
        let isBmark: boolean = false;
        if (oeDataModel.orderType) {
          switch (oeDataModel.orderType.value) {
            case CubiqTS.RequestOrderType.ALGO: {
              isAlgo = true;
              break;
            }
            case CubiqTS.RequestOrderType.BMARK: {
              isBmark = true;
              break;
            }
            default: {
              isLimit = true;
              break;
            }
          }
        }

        if (!this.permissions.permissions.enterTraderGroupLimit && isLimit) {
          this.setter.setTraderGroup(newRefId, null);
          this.setter.setTraderGroupCodeId(newRefId, null);
        }

        if (!this.permissions.permissions.enterTraderGroupAlgo && isAlgo) {
          this.setter.setTraderGroup(newRefId, null);
          this.setter.setTraderGroupCodeId(newRefId, null);
        }

        if (!this.permissions.permissions.enterTraderGroupBenchmark && isBmark) {
          this.setter.setTraderGroup(newRefId, null);
          this.setter.setTraderGroupCodeId(newRefId, null);
        }

        if (
          (isLimit && !this.permissions.permissions.enterMarkupLimit) ||
          (isAlgo && !this.permissions.permissions.enterMarkupAlgo) ||
          (isBmark && !this.permissions.permissions.enterMarkupBMark)
        ) {
          this.setter.setMarkup(newRefId, null);
          oeDataModel.markup = null;
        }
        this.setter.removeModificationPrivatized(newRefId);
        this.setter.removeLockForModification(newRefId);
        this.setter.resetFillState(newRefId);
        this.setter.resetModifyIssuerOrderId(newRefId);
        this.setter.setActivelyDisplayed(newRefId);
        // reset state
        this.setter.setOrderDisplayState(newRefId, OENDisplayState.SHOW_ORDER_ENTRY);

        // cmd
        oeApiService.launchDuplicateOrderPopoutForModify(
          newRefId,
          this.getter.getEntryType(newRefId),
        );

        setTimeout(() => {
          let strategyLegs: Array<string> = this.getter.getStrategyNodesListRoot(newRefId);
          if (strategyLegs.length > 1) {
            strategyLegs.forEach((legRefId) => {
              this.coordinator.cmdAddStrategyLegRefListener(newRefId, legRefId);
            });
            this.coordinator.cmdDisplayStrategyLayoutChange(
              newRefId,
              OENCommand.PANEL_SHOW_STRATEGY,
            );
          }
        }, 100);

        let modelData: OEDataModel = this.getter.get(newRefId); ///this.getter.copyModelOrderEntryInstanceByModel(this.getter.get(refId));
        // prepare to load data by transforming the model to input model array
        let dataToLoad: OEDLoadingDataModel =
          this.utility.createInterfaceLoadDataFromModel(modelData);
        setTimeout(() => {
          this.coordinator.cmdLoadInterfaceControlData(newRefId, dataToLoad);
        }, 250);
        setTimeout(() => {
          let lockProfile = OEPrivatizationLockingProfileModel.getLockIntersection(
            modelData.strategy.lockProfile,
            modelData.privatizationLockProfile,
          );
          this.coordinator.cmdLoadInterfaceControlLockingProfile(newRefId, lockProfile);
        }, 400);

        // mkt strategy buttons
        setTimeout(() => {
          // fix for OMS-35286
          if (
            sourceOperation === OENOperationMode.AMEND_MARKUP ||
            sourceOperation === OENOperationMode.MODIFY_INFO
          ) {
            if (this.getter.isOrderTypeMKT(newRefId)) {
              let mktLockingProfile = this.locker.createLockProfileForMKTOrder(refId);
              this.coordinator.cmdLoadInterfaceControlLockingProfile(refId, mktLockingProfile);
            } else if (this.getter.isOrderTypeMKT(newRefId)) {
              let mktLegLockingProfile = this.locker.createLockProfileForMKTStrategyLeg(refId);
              this.coordinator.cmdLoadInterfaceControlLockingProfile(refId, mktLegLockingProfile);
            }
          }
          this.coordinator.cmdUpdateStrategyButtonsLockingWithMKTCheck(newRefId, true);
          // block updates on new root
          this.modelSync.removeFromBlockUpdateList(newRefId);
        }, 450);
      } catch (ex) {
        logger.out.error(
          "ERROR: _oeActions.actionDuplicateOrder(): Order instance copy failed! reason:",
          ex,
        );
      }
    } else {
      logger.out.error(
        'ERROR: _oeActions.actionDuplicateOrder(): RefId "' + refId + '" invalid or not found!',
      );
    }
  }

  /**********************************************************************/
  /* EDIT  */

  /**
   * IMPORTANT: call this method with the actively selected node not the root (if not selected)
   * @param activeRefId
   * @param setModeToNew set it to true on copy
   */
  public edit(
    activeRefId: string,
    setModeToNew: boolean = false,
    fromEdit: boolean = false,
    scratchpad: boolean = false,
  ): void {
    if (this.getter.has(activeRefId)) {
      let edit: boolean = this.getter.isOperationModeEdit(activeRefId);
      if (
        this.getter.isOperationModeNew(activeRefId) ||
        this.getter.isOperationModeMonitorExecution(activeRefId) ||
        edit ||
        this.getter.isOperationModeDuplicate(activeRefId) || // NEW Order, DUPLICATE Order
        ((this.getter.isOperationModeModify(activeRefId) ||
          this.getter.isOperationModeModifyInfo(activeRefId)) && // MODIFY & MODIFY_INFO after they are finished/submitted (copy, NOT copy..)
          this.getter.isOrderDisplayState(activeRefId, OENDisplayState.SHOW_SUCCESS)) ||
        (fromEdit && scratchpad && this.getter.isOrderDisplayStateTerminal(activeRefId))
      ) {
        //this.getter.isOrderDisplayStateTerminal(activeRefId))){
        ///
        this.copy(activeRefId, !edit, scratchpad && fromEdit);
      } else {
        // MODIFY, MODIFY_INFO Order during active edit
        logger.out.info(
          "DEV-TEST: _oeActions: onEdit lock state for modification: ",
          this.getter.isLockedForModification(activeRefId),
          " for: ",
          activeRefId,
        );
        this.setter.startInInitialization(activeRefId);
        if (this.getter.isLockedForModification(activeRefId)) {
          this.coordinator.cmdChangeDisplayState(activeRefId, OENCommand.STATE_ORDER_ENTRY);
        } else {
          let activateRefIdFg: boolean = false;
          let modifiedRefIds: Map<string, string> = this.operations.regenerateRefs(activeRefId);

          if (modifiedRefIds != null && modifiedRefIds.size > 0) {
            modifiedRefIds.forEach((newRefId: string, originalRefId: string) => {
              // unsubscribe from worker event, delete screen listener & delete
              this.destroyInstance(originalRefId);
              // subscribe to worker events
              this.workerListener.initializeWorkerLifeCycleSubscriptionsForStrategy(newRefId);
              // attach RefId to order entry screen so that it listens for strategy leg
              let rootRefId = this.getter.getRootIdForNodeWithRefId(newRefId);
              this.coordinator.cmdAddStrategyLegRefListener(rootRefId, newRefId);
              // update refId if changed
              if (activeRefId === originalRefId) {
                activeRefId = newRefId;
                activateRefIdFg = true;
              }
            });

            // activate leg if necessary (it necessary if the newly added strategy is actively displayed) and then update strategy render
            if (activateRefIdFg) {
              this.coordinator.cmdActivateStrategyLegRefListener(activeRefId);
              setTimeout(() => {
                this.coordinator.cmdRefreshStrategyLayout(activeRefId);
              }, 200);
            } else {
              this.coordinator.cmdRefreshStrategyLayout(activeRefId);
            }
          }
          //let issuerRefId = this.getter.getModifyIssuerRefId(activeRefId); // issuerRefId used by issueModificationRequestWithoutLoad internally

          this.workerActions
            .issueModificationRequestWithoutLoad(activeRefId)
            .then((acceptanceNeeded: boolean) => {
              this.setter.stopInInitialization(activeRefId);
              /////
              if (acceptanceNeeded == null) {
                this.coordinator.cmdChangeDisplayState(
                  activeRefId,
                  OENCommand.STATE_ORDER_REJECTED,
                );
              } else if (acceptanceNeeded) {
                let traderGroupCodeId: string = this.getter.instance(activeRefId).traderGroupCodeId;
                let srvOrderState: CubiqTS.OrderState =
                  this.getter.instance(activeRefId).srvOrderState;
                let promiseArray = [
                  this.permissions.determineAcceptanceNeededPermissions(
                    CubiqTS.AcceptanceAction.ACCEPT,
                    traderGroupCodeId,
                    srvOrderState,
                  ),
                  this.permissions.determineAcceptanceNeededPermissions(
                    CubiqTS.AcceptanceAction.REJECT,
                    traderGroupCodeId,
                    srvOrderState,
                  ),
                  this.permissions.determineAcceptanceNeededPermissions(
                    CubiqTS.AcceptanceAction.ABORT,
                    traderGroupCodeId,
                    srvOrderState,
                  ),
                ];
                Promise.all(promiseArray)
                  .then(
                    (values: Array<boolean>) => {
                      let acceptPermission: boolean = values[0];
                      let rejectPermission: boolean = values[1];
                      let abortPermission: boolean = values[2];
                      let externalGroupAndState: boolean =
                        this.lookup.determineExternalTraderGroupNow(traderGroupCodeId) &&
                        (srvOrderState === CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED ||
                          srvOrderState === CubiqTS.OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED ||
                          srvOrderState === CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED);

                      //setTimeout(()=> { // if timeout is removed the display state is not applied (oe window not displayed yet)
                      if (externalGroupAndState) {
                        this.coordinator.cmdChangeDisplayState(
                          activeRefId,
                          OENCommand.STATE_ORDER_EXT_MODIFY_OUT_ACCEPTANCE,
                          {
                            acceptance: acceptPermission,
                            rejection: rejectPermission,
                            abort: abortPermission,
                          },
                        );
                        this.coordinator.cmdLockInterfaceControls(activeRefId, false);
                      } else {
                        this.coordinator.cmdChangeDisplayState(
                          activeRefId,
                          OENCommand.STATE_ORDER_MODIFY_OUT_ACCEPTANCE,
                          {
                            acceptance: acceptPermission,
                            rejection: rejectPermission,
                            abort: abortPermission,
                          },
                        );
                        this.coordinator.cmdLockInterfaceControls(activeRefId, false);
                      }
                      //}, 300);
                    },
                    (rejection: any) => {
                      this.coordinator.cmdChangeDisplayState(
                        activeRefId,
                        OENCommand.STATE_ORDER_INT_ERROR,
                        "Internal error: Permission promise rejected!",
                      );
                    },
                  )
                  .catch((reason) => {
                    this.coordinator.cmdChangeDisplayState(
                      activeRefId,
                      OENCommand.STATE_ORDER_INT_ERROR,
                      "Internal error: exception = " + reason,
                    );
                  });
              } else {
                this.coordinator.cmdChangeDisplayState(
                  activeRefId,
                  OENCommand.STATE_ORDER_IN_MODIFICATION,
                );
              }
            });
        }
      }
    } else {
      logger.out.error(
        'ERROR: _oeActions.actionEditOrder(): RefId "' + activeRefId + '" invalid or not found!',
      );
    }
  }

  /**********************************************************************/
  /* RESET  */

  /**
   * This method is a handler when user clicks the clear button on Order Entry Screen.
   *
   */
  public reset(refId: string): boolean {
    if (this.getter.has(refId)) {
      if (
        this.getter.isOperationModeNew(refId) ||
        this.getter.isOperationModeDuplicate(refId) ||
        this.getter.isOperationModeEdit(refId) ||
        this.getter.isOperationModeModifyScratchpad(refId) ||
        this.getter.isSavedOrder(refId)
      ) {
        // NEW, DUPLICATE and EDIT
        let rootRefId = refId;
        if (this.getter.hasStrategy(refId)) {
          rootRefId = this.getter.getRootIdForNodeWithRefId(refId);
          this.coordinator.cmdActivateStrategyLegRefListener(rootRefId);
          let legsToDelete: Array<string> = this.getter.getStrategyNodesListPartial(rootRefId);
          this.destroyInstancesList(legsToDelete);
          setTimeout(() => {
            this.operations.resetInstance(rootRefId);
            this.coordinator.cmdResetAllInterfaceControls(rootRefId);
          }, 100);
          return true;
        } else {
          this.operations.resetInstance(rootRefId);
          if (this.getter.isEntryTypeAlgo(refId) || this.getter.isEntryTypeNdfAlgo(refId)) {
            this.setter.setOrderType(
              rootRefId,
              this.utility.createAutocompleteForOrderType(CubiqTS.RequestOrderType.ALGO),
            );
          }
          this.coordinator.cmdResetAllInterfaceControls(rootRefId);
          return true;
        }
      }
      /* else if(this.getter.isOperationModeModify(refId)){

			} */
    } else {
      logger.out.error(
        'ERROR: _oeActions.actionReset(): RefId "' + refId + '" invalid or not found!',
      );
      return false;
    }
  }

  /**********************************************************************/
  /* SUBMIT  */
  /**
   * This method is a handler when user clicks the submit button on Order Entry Screen.
   */
  public submit(refId: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (this.getter.has(refId)) {
        let updateStrategyDisplay =
          this.getter.hasValidationErrors(refId, true) && !this.getter.isRootNodeAlone(refId);
        if (this.getter.isOperationModeModifyInfo(refId)) {
          this.workerActions.submitModifyInfoRequest(refId).then((result: boolean) => {
            if (result && updateStrategyDisplay) {
              this.coordinator.cmdDisplayStrategyLayoutChange(
                refId,
                OENCommand.PANEL_SHOW_STRATEGY,
              );
            }
            resolve(result);
          });
        } else {
          this.workerActions.submitOrderRequest(refId).then((result: boolean) => {
            if (result && updateStrategyDisplay) {
              this.coordinator.cmdDisplayStrategyLayoutChange(
                refId,
                OENCommand.PANEL_SHOW_STRATEGY,
              );
            }
            resolve(result);
          });
        }
      } else {
        resolve(false);
      }
    });
  }

  /* SAVE ORDER  */
  /**
   * This method is a handler when user clicks the save button on Order Entry Screen.
   */
  public save(refId: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (this.getter.has(refId)) {
        this.workerActions.saveOrderRequest(refId).then((result: boolean) => {
          if (!this.getter.isRootNodeAlone(refId)) {
            // only strats do this
            this.coordinator.cmdRefreshStrategyLayout(refId);
          }
          resolve(result);
        });
      } else {
        resolve(false);
      }
    });
  }

  /**********************************************************************/
  /* ABORT  */
  public abort(refId: string): Promise<CubiqTS.ErrorsImpl[]> {
    return this.workerActions.submitAbort(refId);
  }

  /**********************************************************************/
  /* UNLOCK  */
  public unlockAmendMarkup(orderIds: Array<string>): void {
    this.workerActions.submitUnlockOrdersOnAmendMarkup(orderIds);
  }

  public unlockModify(orderIds: Array<string>, isBmark: boolean = false): void {
    this.workerActions.submitUnlockOrdersOnModify(orderIds, isBmark);
  }

  public unlockOrdersFromDI(refId: string): void {
    this.workerActions.submitUnlockOrdersFromDI(refId);
  }
  /**********************************************************************/
  /* DESTROY  */

  public destroyInstancesList(refIds: Array<string>): void {
    if (refIds != null && Array.isArray(refIds) && refIds.length > 0) {
      refIds.forEach((nodeRefId) => {
        // To: Past Ognen,
        // Subject: OMS-33470,
        // Body: If you gonna rename shit, do it proper! You ass!
        // From: Future Ognen
        this.destroyInstance(nodeRefId);
      });
    }
  }

  //CHANGE destroyAllOrderEntryStrategyInstances()
  public destroyInstances(refId: string, killWorkerSubscription: boolean = true): void {
    let rootRefId = this.getter.getRootIdForNodeWithRefId(refId);
    let strategyNodes: Array<string> = this.getter.getStrategyNodesListRoot(refId);
    strategyNodes.forEach((nodeRefId: string) => {
      this.destroyInstance(nodeRefId, killWorkerSubscription);
    });
    // delete screen command listener (for root)
    if (strategyNodes.length > 1) {
      this.coordinator.cmdRemoveStrategyLegRefListener(rootRefId, rootRefId, true);
    }
  }

  /**
   * DESTROY entry in getter
   */
  protected destroyInstance(refId: string, killWorkerSubscription: boolean = true): void {
    if (this.getter.has(refId)) {
      try {
        // unsubscribe from worker event subscriptions if any
        if (this.getter.isSubscriptionToLifeCycleEventsActive(refId) && killWorkerSubscription) {
          this.workerListener.destroyWorkerInterventionSubscriptionsForStrategy(refId);
          this.workerListener.destroyWorkerLifeCycleSubscriptionsForStrategy(refId);
          this.workerListener.destroyWorkerScratchpadSubscriptionsForStrategy(refId);
        }
        // delete screen command listener (for legs)
        let rootRefId = this.getter.getRootIdForNodeWithRefId(refId);
        if (!this.getter.isRootNode(refId)) {
          this.coordinator.cmdRemoveStrategyLegRefListener(rootRefId, refId);
        }

        // https://www.bcdfx.com/ijira/browse/OMS-28928
        // we need to unlock the order, if it was locked to us in DI.
        let model = this.getter.get(refId);
        if (
          (this.getter.isOperationModeNew(refId) ||
            this.getter.isOperationModeModify(refId) ||
            this.getter.isOperationModeModifyScratchpad(refId) ||
            this.getter.isOperationModeDuplicate(refId)) &&
          model.displayState === OENDisplayState.SHOW_APPROVAL_ALLOWED
        ) {
          this.unlockOrdersFromDI(refId);
        }

        // Abort on logout logic /// REMOVED AS PART OF OMS-28059
        // let model = this.getter.get(refId);
        // if( this.getter.isOperationModeNew(refId) &&
        // 	(model.displayState === OENDisplayState.SHOW_APPROVAL_ALLOWED ||
        //  	 model.displayState === OENDisplayState.SHOW_APPROVAL_EXTERNAL ||
        // 	 model.displayState === OENDisplayState.SHOW_PROCESSING)){
        // 	this.abort(refId);
        // 	setTimeout(()=>{this.operations.deleteInstance(refId);}, 500);
        // }else if( this.getter.isOperationModeNew(refId) && model.displayState === OENDisplayState.SHOW_ACCEPTANCE_ALLOWED && model.traderGroupCodeId != null && this.settings.showDialogOnAbort){
        // 	this.permissions.determineAcceptanceNeededPermissions(CubiqTS.AcceptanceAction.ABORT, model.traderGroupCodeId).then((result: boolean)=>{
        // 		if(result){
        // 			// this.abort(refId);
        // 		}
        // 		setTimeout(()=>{this.operations.deleteInstance(refId);}, 500);
        // 	});
        // }else{
        this.operations.deleteInstance(refId);
        // }
      } catch (ex) {
        logger.out.error(
          `ERROR: _oeActions.destroyOrderEntryInstance(): Order ${refId} deleted! Exception: %O`,
          ex,
        );
        this.operations.deleteInstance(refId);
      }
    }
  }

  /**********************************************************************/
  /* COMPLEX MODEL UPDATES  */

  /* public updateClientTrader(refId: string, _clientTrader: ICAutocompleteGenericModel|CubiqTS.UserImpl): void {
		try{
			if(this.getter.has(refId) && this.getter.get(refId).clientTrader !== _clientTrader) {
				let userCode = null;
				let oldUserCode = null;
				let user = null;
				// reset manual client comment on all clientTrader changes as per KD and Tina
				//this.getter.resetManualClientComment(refId);

				if (_clientTrader instanceof ICAutocompleteGenericModel) {
					userCode = _clientTrader.value;
				} else if (_clientTrader instanceof CubiqTS.UserImpl) {
					userCode = _clientTrader.userCode;
					user = _clientTrader;
				}

				if (this.getter.get(refId).clientTrader instanceof ICAutocompleteGenericModel) {
					oldUserCode = (this.getter.get(refId).clientTrader as ICAutocompleteGenericModel).value;
				} else if (this.getter.get(refId).clientTrader instanceof CubiqTS.UserImpl) {
					oldUserCode = (this.getter.get(refId).clientTrader as CubiqTS.UserImpl).userCode;
				}

				this.setter.setClientTrader(refId, _clientTrader as any);

				if (this.getter.get(refId).clientTrader == null || (_clientTrader != null && userCode !== oldUserCode && userCode != null)) {
					let updateCommentsFn = (refId, user) => {
						//let client = this.getter.get(refId).client;
						let clientTraderComment = null;
						clientTraderComment = user != null && user.comment != null && user.comment.length > 0 ? `${user.userName}: ${user.comment}` : null;

					//	if (clientTraderComment) {
							this.setter.setClientTraderClientComment(refId, clientTraderComment);
							this.coordinator.cmdPropagateClientDefaultCommentsChange(refId);
					//	}
					};

					if (user) {
						updateCommentsFn(refId, user);
					} else {
						this.lookup.user(userCode)
							.then((user: CubiqTS.UserImpl) => {
								updateCommentsFn(refId, user);
						});
					}
				}
			}
		}catch(ex){
			logger.out.error('ERROR: _oeActions.updateClientTrader(): exception: ',ex.message);
		}
	}


	public updateClient(refId: string, _clientData: CubiqTS.ClientImpl | null): void {
		try{
			if(this.getter.has(refId) && this.getter.get(refId).client !== _clientData){
				// object equality not always assured in strategy context
				// check id before updating comments
				let currentClient = this.getter.get(refId).client;
				let isClientChange = currentClient == null || (_clientData != null && currentClient.clientId !== _clientData.clientId);

				this.setter.setClient(refId, _clientData);

				if( this.getter.get(refId).orderType != null && this.getter.get(refId).orderType.value === CubiqTS.RequestOrderType.TP ){ // if TP update state of the allowFillsCheckbox
				//	this.coordinator.cmdShowAllowPartialFillsCheckbox(refId);
				}

				if (isClientChange) {
					this.setter.setDefaultComments(refId, _clientData.defaultClientComment, _clientData.defaultPrivateComment);
					//this.getter.resetManualClientComment(refId);
				}
			}
			//this.coordinator.cmdPropagateClientChange(refId);
		}catch(ex){
			logger.out.error('ERROR: _oeActions.updateClient(): exception: ', ex.message);
		}
	} */

  /**********************************************************************/
  /*********************** STRATEGIES ***********************************/
  /**********************************************************************/

  /**********************************************************************/
  /* CREATE RELATIONSHIPS */

  public createTimeoutRelationship(parentRefId: string): void {
    try {
      if (this.getter.has(parentRefId) && this.getter.isRelationshipAllowedTimeout(parentRefId)) {
        let errors = new Array<OEInputModel>();
        let dataValid = this.validator.validateOrderData(parentRefId, errors);
        let goodUntilValid = this.validator.validateGoodUntilForAddingTimeout(parentRefId, errors);

        if (dataValid && goodUntilValid) {
          let legRefId = this.operations.copyInstance(parentRefId);
          let outcome = this.operations.invertOrderTypeForStrategy(
            legRefId,
            CubiqTS.OrderRelationshipType.ON_TIMEOUT,
          );
          let leg = this.getter.instance(legRefId);
          if (leg != null && leg.goodUntil != null) {
            leg.goodUntil.dateValue = null;
            leg.goodUntil.timeValue = null;
            leg.goodUntil.timezoneValue = null;
            leg.goodUntil.marketValue = "GTC";
          }
          if (outcome) {
            // Fix allow partial flag when order type is inverted to TP
            this.operations.checkAllowPartialOnCopiedInstance(parentRefId, legRefId);
            // link the parent with strategyLeg order, set root and parent, set strategy permissions, attach locking profile
            this.operations.addRelationshipTimeout(parentRefId, legRefId);
            // load, display and select strategy
            this.loader.activateStrategyLeg(parentRefId, legRefId);
            // update screen registry monitor with the new node
            this.screenRegistry.updateFull(parentRefId);
          } else {
            this.destroyInstance(legRefId);
            logger.out.error(
              "ERROR: _oeActions.actionCreateTimeout(): Create OnTimeout failed on invert!",
            );
          }
        } else {
          this.coordinator.cmdUnlockInterfaceControls(parentRefId);
          setTimeout(() => {
            this.coordinator.cmdInvalidInput(parentRefId, errors);
          }, 100);
          logger.out.warn(
            `WARNING: _oeActions.actionCreateTimeout(): not permitted to create Timeout: Invalid Input! errors= %O`,
            errors,
          );
        }
      } else {
        // validation rules have failed to disable button for creating Loop!
        logger.out.error(
          "ERROR: _oeActions.actionCreateTimeout(): not permitted to create Timeout",
        );
      }
    } catch (ex) {
      logger.out.error(`ERROR: _oeActions.actionCreateTimeout(): Exception = %O`, ex);
    }
  }

  public createLoopRelationship(parentRefId: string): void {
    try {
      if (this.getter.has(parentRefId) && this.getter.isRelationshipAllowedLoop(parentRefId)) {
        let errors = new Array<OEInputModel>();
        let dataValid = this.validator.validateOrderData(parentRefId, errors);
        if (dataValid) {
          let legRefId = this.operations.copyInstance(parentRefId);
          let outcome = this.operations.invertOrderTypeForStrategy(
            legRefId,
            CubiqTS.OrderRelationshipType.LOOP,
          );
          if (outcome) {
            // Fix allow partial flag when order type is inverted to TP
            this.operations.checkAllowPartialOnCopiedInstance(parentRefId, legRefId);
            // link the parent with strategyLeg order, set root and parent, set strategy permissions, attach locking profile
            this.operations.addRelationshipLoop(parentRefId, legRefId);
            // load, display and select strategy
            this.loader.activateStrategyLeg(parentRefId, legRefId);
            // update screen registry monitor with the new node
            this.screenRegistry.updateFull(parentRefId);
          } else {
            this.destroyInstance(legRefId);
            logger.out.error("ERROR: _oeActions.actionCreateLoop(): Create Loop failed on invert!");
          }
        } else {
          this.coordinator.cmdUnlockInterfaceControls(parentRefId);
          setTimeout(() => {
            this.coordinator.cmdInvalidInput(parentRefId, errors);
          }, 100);
          logger.out.warn(
            "WARNING: _oeActions.actionCreateLoop(): not permitted to create Loop: Invalid Input! errors= %O",
            errors,
          );
        }
      } else {
        // validation rules have failed to disable button for creating Loop!
        logger.out.error("ERROR: _oeActions.actionCreateLoop(): not permitted to create Loop");
      }
    } catch (ex) {
      logger.out.error("ERROR: _oeActions.actionCreateLoop(): Exception = %O", ex);
    }
  }

  public createIfDoneRelationship(parentRefId: string): void {
    try {
      if (this.getter.has(parentRefId) && this.getter.isRelationshipAllowedIfDone(parentRefId)) {
        let errors = new Array<OEInputModel>();
        let dataValid = this.validator.validateOrderData(parentRefId, errors);
        if (dataValid) {
          // create duplicate of the root order to create strategyLeg order
          let legRefId = this.operations.copyInstance(parentRefId);
          let outcome = this.operations.invertOrderTypeForStrategy(
            legRefId,
            CubiqTS.OrderRelationshipType.IFDONE,
          );
          if (outcome) {
            // Fix allow partial flag when order type is inverted to TP
            this.operations.checkAllowPartialOnCopiedInstance(parentRefId, legRefId);
            // link the parent with strategyLeg order, set root and parent, set strategy permissions, attach locking profile
            this.operations.addRelationshipIfDone(parentRefId, legRefId);
            // load, display and select strategy
            this.loader.activateStrategyLeg(parentRefId, legRefId);
            // update screen registry monitor with the new node
            this.screenRegistry.updateFull(parentRefId);
          } else {
            this.destroyInstance(legRefId);
            logger.out.error(
              "ERROR: _oeActions.actionCreateIfDone(): Create IfDone failed on invert order type and intent!",
            );
          }
        } else {
          this.coordinator.cmdUnlockInterfaceControls(parentRefId);
          setTimeout(() => {
            this.coordinator.cmdInvalidInput(parentRefId, errors);
          }, 100);

          logger.out.warn(
            `WARNING: _oeActions.actionCreateIfDone(): not permitted to create IfDone: Invalid Input! errors= %O`,
            errors,
          );
        }
      } else {
        // validation rules have failed to disable button for creating IfDone!
        logger.out.error("ERROR: _oeActions.actionCreateIfDone(): not permitted to create IfDone");
      }
    } catch (ex) {
      logger.out.error(`ERROR: _oeActions.actionCreateIfDone(): Exception = %O`, ex);
    }
  }

  public createOcoRelationship(parentRefId: string): void {
    try {
      if (this.getter.has(parentRefId) && this.getter.isRelationshipAllowedOco(parentRefId)) {
        let errors = new Array<OEInputModel>();
        let dataValid = this.validator.validateOrderData(parentRefId, errors);

        if (dataValid) {
          // create duplicate of the root order to create strategyLeg order
          let legRefId = this.operations.copyInstance(parentRefId);
          // modify/invert the strategyLeg order
          let outcome = this.operations.invertOrderTypeForStrategy(
            legRefId,
            CubiqTS.OrderRelationshipType.OCO,
          );
          if (outcome) {
            // Fix allow partial flag when order type is inverted to TP
            this.operations.checkAllowPartialOnCopiedInstance(parentRefId, legRefId);
            // link the parent with strategyLeg order, set strategy permissions, attach locking profile
            this.operations.addRelationshipOco(parentRefId, legRefId);
            // load, display and select strategy
            this.loader.activateStrategyLeg(parentRefId, legRefId);
            // update screen registry monitor with the new node
            this.screenRegistry.updateFull(parentRefId);
          } else {
            this.destroyInstance(legRefId);
            logger.out.error(
              "ERROR: _oeActions.actionCreateOco(): Create OCO failed on invert order type!",
            );
          }
        } else {
          this.coordinator.cmdUnlockInterfaceControls(parentRefId);
          setTimeout(() => {
            this.coordinator.cmdInvalidInput(parentRefId, errors);
          }, 100);

          logger.out.warn(
            `WARNING: _oeActions.actionCreateOco(): not permitted to create OCO: Invalid Input! errors= %O`,
            errors,
          );
        }
      } else {
        // validation rules have failed to disable button for creating Oco!
        logger.out.error("ERROR: _oeActions.actionCreateOco(): not permitted to create OCO");
      }
    } catch (ex) {
      logger.out.error(`ERROR: _oeActions.actionCreateOco(): Exception = %O`, ex);
    }
  }

  /**********************************************************************/
  /* REMOVE STRATEGY NODE */

  public removeStrategy(refId: string): void {
    if (this.getter.hasStrategy(refId)) {
      if (this.getter.getDisplayStateForRoot(refId) === OENDisplayState.SHOW_ORDER_ENTRY) {
        try {
          //logger.out.info("DEV-TEST: actionRemoveStrategy()-> START: ModelMap = ", Object.assign(this.getter.getAll()));
          let parentRefId: string = this.getter.get(refId).strategy.parentRefId;
          let parentModel: OEDataModel = this.getter.get(parentRefId);
          let strategyLegsToRemove: Array<string> = this.getter.getStrategyNodesListPartial(refId);

          //check if the modify issuer is being removed, if so switch everything to the parent (recursively) apply new locking profile

          // activate listener, load parent data and parent locking profile
          this.loader.actionLoadStrategy(parentRefId);

          strategyLegsToRemove.forEach((itemRefId: string) => {
            if (!this.getter.isRootNode(itemRefId) && itemRefId !== parentRefId) {
              // remove relationship to child node in parent node
              let parentsChildIndex: number = parentModel.strategy.children
                .map((item) => {
                  return item.refId;
                })
                .indexOf(itemRefId);
              if (parentsChildIndex >= 0) {
                this.operations.removeChildRelationship(parentRefId, parentsChildIndex);
              }
              // remove relationship to sibling/spouse in parent node
              let parentsSiblingIndex: number = this.getter
                .get(parentRefId)
                .strategy.siblings.map((item) => {
                  return item.refId;
                })
                .indexOf(itemRefId); // item instanceof : BfxOrderEntryStrategyRefModel
              if (parentsSiblingIndex >= 0) {
                this.operations.removeSiblingRelationship(parentRefId, parentsSiblingIndex);
              }
              // delete the node
              if (
                this.getter.isOperationModeModify(itemRefId) &&
                this.getter.isThisTheModifyIssuer(itemRefId)
              ) {
                this.sync.registerForward(itemRefId, parentRefId);
                this.setter.setModifyIssuerOrderId(parentRefId, parentModel.orderId);
                this.destroyInstance(itemRefId, false);
              } else {
                this.destroyInstance(itemRefId);
              }

              this.coordinator.cmdRemoveStrategyLegRefListener(parentRefId, itemRefId);
              logger.out.info(
                "INFO: _oeActions.actionRemoveStrategy(): '" + itemRefId + "' node deleted!",
              );
            } else {
              logger.out.error(
                "ERROR: _oeActions.actionRemoveStrategy(): Invalid graph exploration refId '" +
                  itemRefId +
                  "' should not be in the list!",
              );
            }
          });
          // update screen registry monitor with the new node
          this.screenRegistry.updateFull(parentRefId);
          // handle case when parent is root
          if (this.getter.isRootNodeAlone(parentRefId)) {
            // no need to reset the strategy model (it is correct),
            // in fact reset deletes the locking profile which creates problems with the introduction MKT orders
            //parentModel.strategy = new OEStrategyModel();
            this.coordinator.cmdDisplayStrategyLayoutChange(
              parentRefId,
              OENCommand.PANEL_HIDE_STRATEGY,
            );
          } else {
            // update layout
            this.coordinator.cmdDisplayStrategyLayoutChange(
              parentRefId,
              OENCommand.PANEL_SHOW_STRATEGY,
            );
          }

          //logger.out.info("DEV-TEST: _oeActions.actionRemoveStrategy()-> END: ModelMap = ",  Object.assign(this.getter.getAll()));
        } catch (ex) {
          logger.out.error("ERROR: _oeActions.actionRemoveStrategy(): Exception = ", ex);
        }
      } else {
        logger.out.info(
          "_oeActions.actionRemoveStrategy(): DELETE REFUSED -> WRONG STATE! | Model state = ",
          this.getter.getDisplayState(refId),
        );
      }
    }
  }

  /**********************************************************************/
  /* DEBUG - HELPER  */

  private outputExtendedLogging(value: string, dump: any = null): void {
    this.oeLogger.outputExtendedLogging(value, dump);
  }
}
